/**
 * Déports — refonte UX, chantier 4 (maquette 11, validée le 24/09).
 *
 * Un élu en situation de conflit d'intérêts est suppléé ; il ne voit plus les
 * dossiers concernés et sa signature va à son suppléant (tracé). Deux voies :
 *  · ARRÊTÉ DE DÉPORT (cas général, décret n° 2014-90) : le suppléant est le délégataire ;
 *  · DÉLIBÉRATION DU CONSEIL (autorisation d'urbanisme où le maire est intéressé,
 *    C. urb. L422-7) : le conseil désigne un autre de ses membres.
 * Dossiers pris en charge par l'écran : demandes d'urbanisme et fiches du service
 * technique (le serveur accepte d'autres types, ajoutés au fil des briques).
 */
import { useEffect, useState } from 'react';
import { EyeOff } from 'lucide-react';
import { Card, Button, Badge, Input } from '@/components/ui';
import { api, ApiException, type DeportDTO, type ProfilGouvDTO } from '@/lib/api';

type Entite = { type: string; id: string; libelle: string };

export function DeportsPage() {
  const [deports, setDeports] = useState<DeportDTO[]>([]);
  const [personnes, setPersonnes] = useState<ProfilGouvDTO[]>([]);
  const [demandes, setDemandes] = useState<Array<{ id: string; numero: string }>>([]);
  const [fiches, setFiches] = useState<Array<{ id: string; numero: string; sujet: string }>>([]);
  const [delibs, setDelibs] = useState<Array<{ id: string; libelle: string }>>([]);
  const [elu, setElu] = useState(''); const [suppleant, setSuppleant] = useState('');
  const [voie, setVoie] = useState<'arrete' | 'deliberation'>('arrete');
  const [objet, setObjet] = useState(''); const [arrete, setArrete] = useState(''); const [delib, setDelib] = useState('');
  const [entites, setEntites] = useState<Entite[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);

  const charger = () => api.gouvernance.deports().then((r) => setDeports(r.deports)).catch(() => setDeports([]));
  useEffect(() => {
    void charger();
    api.gouvernance.profils().then((r) => setPersonnes(r.profils)).catch(() => setPersonnes([]));
    api.urbanisme.listDemandes().then((r) => setDemandes(r.demandes.map((d) => ({ id: d.id, numero: d.numero })))).catch(() => setDemandes([]));
    api.serviceTechnique.signalements.list({ estEtatDesLieux: false })
      .then((r) => setFiches((r.items ?? []).map((x) => ({ id: x.id, numero: x.numero, sujet: x.sujet })))).catch(() => setFiches([]));
    api.deliberations.registre().then((r) => setDelibs(r.deliberations.map((d) => ({ id: d.id, libelle: `${d.numero ?? 'Projet'} · ${d.objet}` })))).catch(() => setDelibs([]));
  }, []);

  const ajouter = (e: Entite) => setEntites((l) => (l.some((x) => x.id === e.id) ? l : [...l, e]));

  async function enregistrer() {
    setMsg(null);
    try {
      await api.gouvernance.creerDeport({
        eluUserId: elu, voie, objet: objet.trim(), suppleantUserId: suppleant,
        arreteReference: voie === 'arrete' ? (arrete.trim() || null) : null,
        deliberationId: voie === 'deliberation' ? (delib || null) : null,
        entites: entites.map(({ type, id, libelle }) => ({ type, id, libelle })),
      });
      setMsg({ ok: true, t: 'Déport enregistré : l’élu ne voit plus ces dossiers.' });
      setObjet(''); setArrete(''); setDelib(''); setEntites([]);
      await charger();
    } catch (e) { setMsg({ ok: false, t: e instanceof ApiException ? e.payload.error : 'Enregistrement impossible' }); }
  }

  const pret = elu && suppleant && elu !== suppleant && objet.trim().length >= 3 && entites.length > 0 && (voie === 'arrete' || !!delib);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-semibold text-ink">Déports</h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">Un élu déporté ne voit plus les dossiers concernés ; sa signature va à son suppléant. Tout est tracé.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_460px]">
        <Card variant="glass" className="overflow-hidden p-0">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold text-ink">Déports</div>
          {deports.length === 0 && <p className="px-4 py-4 text-sm text-ink-subtle">Aucun déport enregistré.</p>}
          <ul>
            {deports.map((d) => (
              <li key={d.id} className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
                <EyeOff className={`h-4 w-4 ${d.actif ? 'text-urgent' : 'text-ink-subtle'}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-ink">{d.eluNom} · {d.objet}</p>
                  <p className="text-2xs text-ink-muted">Suppléé par {d.suppleantNom} · depuis le {d.debutLe.split('-').reverse().join('/')}
                    {' · '}{d.entites.map((e) => e.libelle ?? e.type).join(', ')}</p>
                </div>
                <Badge variant={d.voie === 'deliberation' ? 'info' : 'neutral'} size="sm">{d.voie === 'deliberation' ? 'Délibération du conseil' : 'Arrêté de déport'}</Badge>
                {d.actif
                  ? <Button size="sm" variant="ghost" onClick={() => void api.gouvernance.cloreDeport(d.id).then(charger)}>Clore</Button>
                  : <Badge variant="neutral" size="sm">Clos</Badge>}
              </li>
            ))}
          </ul>
          <p className="px-4 py-3 text-2xs text-ink-subtle">Cas général : arrêté de déport (décret n° 2014-90). Autorisation d’urbanisme où le maire est intéressé : le conseil municipal désigne un autre de ses membres, par délibération (code de l’urbanisme, art. L422-7).</p>
        </Card>

        <Card variant="glass" padded>
          <h2 className="mb-3 text-base font-semibold text-ink">Nouveau déport</h2>
          <div className="space-y-3">
            <label className="block text-xs text-ink">Élu concerné
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={elu} onChange={(e) => setElu(e.target.value)}>
                <option value="">— Choisir —</option>{personnes.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select></label>
            <label className="block text-xs text-ink">Objet
              <Input className="mt-1" value={objet} maxLength={1000} placeholder="Ex : permis de la SCI familiale" onChange={(e) => setObjet(e.target.value)} /></label>
            <fieldset className="space-y-2"><legend className="text-xs text-ink">Voie juridique</legend>
              {([['arrete', 'Arrêté de déport', 'Cas général. Le suppléant est le délégataire de l’élu.'],
                 ['deliberation', 'Délibération du conseil municipal', 'Autorisation d’urbanisme où le maire est intéressé. Le conseil désigne un autre de ses membres.']] as const).map(([v, l, sous]) => (
                <label key={v} className={`flex gap-2 rounded-r-lg border p-3 ${voie === v ? 'border-tech bg-tech/10' : 'border-border'}`}>
                  <input type="radio" name="voie" checked={voie === v} onChange={() => setVoie(v)} className="mt-1" />
                  <span><span className="block text-sm font-medium text-ink">{l}</span><span className="block text-2xs text-ink-muted">{sous}</span></span>
                </label>))}
            </fieldset>
            {voie === 'arrete'
              ? <label className="block text-xs text-ink">Référence de l’arrêté<Input className="mt-1" value={arrete} maxLength={120} onChange={(e) => setArrete(e.target.value)} /></label>
              : <label className="block text-xs text-ink">Délibération de désignation
                  <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={delib} onChange={(e) => setDelib(e.target.value)}>
                    <option value="">— Choisir —</option>{delibs.map((d) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
                  </select></label>}
            <label className="block text-xs text-ink">Suppléant
              <select className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={suppleant} onChange={(e) => setSuppleant(e.target.value)}>
                <option value="">— Choisir —</option>{personnes.filter((p) => p.id !== elu).map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select></label>
            <div className="space-y-2">
              <span className="block text-xs text-ink">Dossiers concernés</span>
              <select aria-label="Ajouter une demande d’urbanisme" className="h-9 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value=""
                onChange={(e) => { const d = demandes.find((x) => x.id === e.target.value); if (d) ajouter({ type: 'urba_demande', id: d.id, libelle: d.numero }); }}>
                <option value="">+ Une demande d’urbanisme…</option>{demandes.map((d) => <option key={d.id} value={d.id}>{d.numero}</option>)}
              </select>
              <select aria-label="Ajouter une fiche du service technique" className="h-9 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value=""
                onChange={(e) => { const f = fiches.find((x) => x.id === e.target.value); if (f) ajouter({ type: 'signalement', id: f.id, libelle: `${f.numero} ${f.sujet}` }); }}>
                <option value="">+ Une fiche du service technique…</option>{fiches.map((f) => <option key={f.id} value={f.id}>{f.numero} — {f.sujet}</option>)}
              </select>
              <div className="flex flex-wrap gap-1.5">{entites.map((e) => (
                <button key={e.id} type="button" onClick={() => setEntites((l) => l.filter((x) => x.id !== e.id))}
                  className="rounded-r-pill border border-border px-2.5 py-1 text-2xs text-ink" aria-label={`Retirer ${e.libelle}`}>{e.libelle} ✕</button>))}
              </div>
            </div>
            {msg && <p className={`text-xs ${msg.ok ? 'text-success' : 'text-urgent'}`} role={msg.ok ? 'status' : 'alert'}>{msg.t}</p>}
            <Button size="sm" variant="primary" disabled={!pret} onClick={() => void enregistrer()}>Enregistrer le déport</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
